/**
 * Cubic Bézier evaluation and arc-length reparameterization.
 *
 * Shared by the curve fitter, the GPU sample scheduler, the CPU reference
 * renderer and the SVG exporter, so all of them agree on exactly what
 * "the point at normalized arc length T" means.
 *
 * Strokes store control points flattened as `[x0, y0, x1, y1, …]`; segment `k`
 * spans points `3k … 3k + 3`.
 */

export interface Vec2 {
  x: number
  y: number
}

/** Number of cubic segments in a flattened control-point array. */
export function segmentsIn(points: ArrayLike<number>): number {
  return (points.length / 2 - 1) / 3
}

/** Cubic Bernstein basis at `u`, ordered `[B0, B1, B2, B3]`. */
export function bernstein(u: number): [number, number, number, number] {
  const v = 1 - u
  return [v * v * v, 3 * v * v * u, 3 * v * u * u, u * u * u]
}

/** Derivative of the Bernstein basis at `u`, ordered `[B0', B1', B2', B3']`. */
export function bernsteinDerivative(u: number): [number, number, number, number] {
  const v = 1 - u
  return [-3 * v * v, 3 * v * v - 6 * v * u, 6 * v * u - 3 * u * u, 3 * u * u]
}

export function pointAt(points: ArrayLike<number>, seg: number, u: number, out?: Vec2): Vec2 {
  const i = seg * 6
  const [b0, b1, b2, b3] = bernstein(u)
  const x = b0 * points[i] + b1 * points[i + 2] + b2 * points[i + 4] + b3 * points[i + 6]
  const y = b0 * points[i + 1] + b1 * points[i + 3] + b2 * points[i + 5] + b3 * points[i + 7]
  if (out) {
    out.x = x
    out.y = y
    return out
  }
  return { x, y }
}

export function tangentAt(points: ArrayLike<number>, seg: number, u: number, out?: Vec2): Vec2 {
  const i = seg * 6
  const [d0, d1, d2, d3] = bernsteinDerivative(u)
  const x = d0 * points[i] + d1 * points[i + 2] + d2 * points[i + 4] + d3 * points[i + 6]
  const y = d0 * points[i + 1] + d1 * points[i + 3] + d2 * points[i + 5] + d3 * points[i + 7]
  if (out) {
    out.x = x
    out.y = y
    return out
  }
  return { x, y }
}

export function secondDerivativeAt(
  points: ArrayLike<number>,
  seg: number,
  u: number,
  out?: Vec2,
): Vec2 {
  const i = seg * 6
  const a = 6 * (1 - u)
  const b = 6 * u
  // B''(u) = 6(1-u)(P0 - 2P1 + P2) + 6u(P1 - 2P2 + P3)
  const x =
    a * (points[i] - 2 * points[i + 2] + points[i + 4]) +
    b * (points[i + 2] - 2 * points[i + 4] + points[i + 6])
  const y =
    a * (points[i + 1] - 2 * points[i + 3] + points[i + 5]) +
    b * (points[i + 3] - 2 * points[i + 5] + points[i + 7])
  if (out) {
    out.x = x
    out.y = y
    return out
  }
  return { x, y }
}

/** Signed curvature κ = (x'y'' − y'x'') / |r'|³. Zero for degenerate segments. */
export function curvatureAt(points: ArrayLike<number>, seg: number, u: number): number {
  const d1 = tangentAt(points, seg, u)
  const d2 = secondDerivativeAt(points, seg, u)
  const speed = Math.hypot(d1.x, d1.y)
  if (speed < 1e-9) return 0
  return (d1.x * d2.y - d1.y * d2.x) / (speed * speed * speed)
}

/** Unit normal (left of the tangent). Falls back to `(0, 0)` at cusps. */
export function normalAt(points: ArrayLike<number>, seg: number, u: number): Vec2 {
  const d = tangentAt(points, seg, u)
  const speed = Math.hypot(d.x, d.y)
  if (speed < 1e-9) return { x: 0, y: 0 }
  return { x: -d.y / speed, y: d.x / speed }
}

// ---------------------------------------------------------------------------
// Arc length
// ---------------------------------------------------------------------------

/** Subdivisions per segment in the arc-length table; a power of two keeps the
 * inverse mapping's linear interpolation cheap and exact at the knots. */
export const ARC_SAMPLES_PER_SEGMENT = 32

export interface ArcLengthTable {
  segments: number
  samplesPerSegment: number
  /** Cumulative length at each sample, `segments * (samplesPerSegment + 1)`
   * entries, measured from the start of the whole spline. */
  cumulative: Float64Array
  /** Arc length of each segment. */
  segmentLengths: Float64Array
  total: number
}

/**
 * Builds a monotone arc-length table by chordal sampling.
 *
 * Chordal sampling underestimates length by O(h²); at 32 samples per segment
 * that is well under a thousandth of a pixel for the curvatures line art
 * produces, and — more importantly — it is exactly what {@link locateArcLength}
 * inverts, so forward and inverse mappings stay consistent.
 */
export function buildArcLengthTable(
  points: ArrayLike<number>,
  samplesPerSegment: number = ARC_SAMPLES_PER_SEGMENT,
): ArcLengthTable {
  const segments = segmentsIn(points)
  const stride = samplesPerSegment + 1
  const cumulative = new Float64Array(segments * stride)
  const segmentLengths = new Float64Array(segments)

  let total = 0
  const prev = { x: 0, y: 0 }
  const cur = { x: 0, y: 0 }
  for (let seg = 0; seg < segments; seg++) {
    pointAt(points, seg, 0, prev)
    cumulative[seg * stride] = total
    for (let i = 1; i <= samplesPerSegment; i++) {
      pointAt(points, seg, i / samplesPerSegment, cur)
      total += Math.hypot(cur.x - prev.x, cur.y - prev.y)
      cumulative[seg * stride + i] = total
      prev.x = cur.x
      prev.y = cur.y
    }
    segmentLengths[seg] = total - cumulative[seg * stride]
  }

  return { segments, samplesPerSegment, cumulative, segmentLengths, total }
}

export interface ArcLocation {
  seg: number
  u: number
}

/**
 * Inverts the arc-length table: finds the `(segment, u)` at distance `s` from
 * the start. Clamps to the endpoints outside `[0, total]`.
 */
export function locateArcLength(table: ArcLengthTable, s: number, out?: ArcLocation): ArcLocation {
  const result = out ?? { seg: 0, u: 0 }
  const { segments, samplesPerSegment, cumulative } = table
  const stride = samplesPerSegment + 1

  if (!(s > 0)) {
    result.seg = 0
    result.u = 0
    return result
  }
  if (s >= table.total) {
    result.seg = segments - 1
    result.u = 1
    return result
  }

  // Binary search for the last sample whose cumulative length is <= s. The
  // table is a single monotone sequence across segments, so one search suffices.
  let lo = 0
  let hi = segments * stride - 1
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1
    if (cumulative[mid] <= s) lo = mid
    else hi = mid - 1
  }

  let seg = Math.floor(lo / stride)
  let i = lo - seg * stride
  // A sample sitting exactly on a segment boundary belongs to the next segment.
  if (i === samplesPerSegment) {
    if (seg + 1 < segments) {
      seg += 1
      i = 0
    } else {
      result.seg = seg
      result.u = 1
      return result
    }
  }

  const a = cumulative[seg * stride + i]
  const b = cumulative[seg * stride + i + 1]
  const frac = b > a ? (s - a) / (b - a) : 0
  result.seg = seg
  result.u = (i + frac) / samplesPerSegment
  return result
}

/** Position at normalized arc length `t ∈ [0, 1]`. */
export function pointAtArcFraction(
  points: ArrayLike<number>,
  table: ArcLengthTable,
  t: number,
  out?: Vec2,
): Vec2 {
  const loc = locateArcLength(table, t * table.total)
  return pointAt(points, loc.seg, loc.u, out)
}

/**
 * Samples a spline into a polyline at approximately `spacing` pixels.
 *
 * Returns at least two points; used for rasterization, Chamfer distance and
 * outline offsetting.
 */
export function flatten(points: ArrayLike<number>, spacing: number): Float32Array {
  const table = buildArcLengthTable(points)
  const count = Math.max(2, Math.ceil(table.total / Math.max(spacing, 1e-3)) + 1)
  const out = new Float32Array(count * 2)
  const p = { x: 0, y: 0 }
  const loc: ArcLocation = { seg: 0, u: 0 }
  for (let i = 0; i < count; i++) {
    locateArcLength(table, (i / (count - 1)) * table.total, loc)
    pointAt(points, loc.seg, loc.u, p)
    out[i * 2] = p.x
    out[i * 2 + 1] = p.y
  }
  return out
}

/**
 * Subdivides segment `seg` at `u` (de Casteljau), returning the two halves'
 * control points as flat 8-element arrays.
 */
export function splitSegment(
  points: ArrayLike<number>,
  seg: number,
  u: number,
): [Float64Array, Float64Array] {
  const i = seg * 6
  const p = [
    [points[i], points[i + 1]],
    [points[i + 2], points[i + 3]],
    [points[i + 4], points[i + 5]],
    [points[i + 6], points[i + 7]],
  ]
  const lerp = (a: number[], b: number[]) => [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u]
  const q0 = lerp(p[0], p[1])
  const q1 = lerp(p[1], p[2])
  const q2 = lerp(p[2], p[3])
  const r0 = lerp(q0, q1)
  const r1 = lerp(q1, q2)
  const s = lerp(r0, r1)
  return [
    Float64Array.from([...p[0], ...q0, ...r0, ...s]),
    Float64Array.from([...s, ...r1, ...q2, ...p[3]]),
  ]
}

/**
 * One-sided Chamfer distance from polyline `a` to polyline `b`: the mean
 * distance from each vertex of `a` to the nearest *segment* of `b`.
 */
export function chamferDistance(a: ArrayLike<number>, b: ArrayLike<number>): number {
  const n = a.length / 2
  const m = b.length / 2
  if (n === 0 || m === 0) return Infinity
  let sum = 0
  for (let i = 0; i < n; i++) {
    const px = a[i * 2]
    const py = a[i * 2 + 1]
    let best = Infinity
    for (let j = 0; j + 1 < m; j++) {
      const d = pointSegmentDistanceSq(px, py, b[j * 2], b[j * 2 + 1], b[j * 2 + 2], b[j * 2 + 3])
      if (d < best) best = d
    }
    if (m === 1) best = (px - b[0]) ** 2 + (py - b[1]) ** 2
    sum += Math.sqrt(best)
  }
  return sum / n
}

/** Symmetric Chamfer distance: the larger of the two one-sided means. */
export function symmetricChamfer(a: ArrayLike<number>, b: ArrayLike<number>): number {
  return Math.max(chamferDistance(a, b), chamferDistance(b, a))
}

export function pointSegmentDistanceSq(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax
  const dy = by - ay
  const lenSq = dx * dx + dy * dy
  let t = lenSq > 0 ? ((px - ax) * dx + (py - ay) * dy) / lenSq : 0
  t = t < 0 ? 0 : t > 1 ? 1 : t
  const cx = ax + t * dx - px
  const cy = ay + t * dy - py
  return cx * cx + cy * cy
}
